import { environment } from 'src/environments/environment';

/**
 * Proctoring toggle key -> the client flag that governs it. Mostly the Monitoring
 * section, plus `faceDetection` from HR's Editable Settings panel.
 *
 * A client with the flag off (e.g. LINDE) has that step suppressed at exam time
 * regardless of what the drive/assessment was configured with, so the toggle is
 * forced off and locked in the admin/HR UIs rather than letting someone switch
 * on something the candidate will never see.
 *
 * Keys are the camelCase form used by both settings maps - Create Assessment
 * builds them in transformSettings(), Schedule Drive via toCamelCase().
 */
export const MONITORING_SETTING_FLAGS: { [key: string]: boolean } = {
  aadharPhotoCapture: environment.showFaceCapture,
  screenShare: environment.showScreenShare,
  video: environment.showCameraCheck,
  mobileFeedCapture: environment.showMobileFeedCapture,
  audio: environment.showAudioCheck,
  verifySurroundings: environment.showVerifySurroundings,
  devicesCompatibilityCheck: environment.showDeviceCompatibilityCheck,
  faceDetection: environment.showFaceCapture,
};

/** True when this client cannot use the given proctoring setting at all. */
export function isProctoringSettingLocked(key: string): boolean {
  return MONITORING_SETTING_FLAGS[key] === false;
}

/**
 * Forces every locked key in a settings map to false. Call this wherever a
 * settings map is built or reloaded, so a previously-saved drive/assessment
 * cannot bring a suppressed step back on.
 */
export function applyProctoringSettingLocks(
  settings: Map<string, boolean>
): Map<string, boolean> {
  settings.forEach((value, key) => {
    if (isProctoringSettingLocked(key)) {
      settings.set(key, false);
    }
  });
  return settings;
}

/**
 * Forces every client-locked step off on a live proctoring-settings DTO, which
 * still arrives from the API with those steps enabled. Call it on every
 * `proctoringSettingDto` - the connected-drive one was missed, and its tab index
 * sent LINDE candidates to the photo-capture step. Mutates and returns the DTO.
 */
export function applyClientProctoringFlags<T>(dto: T): T {
  if (!dto) {
    return dto;
  }
  Object.keys(MONITORING_SETTING_FLAGS).forEach((key) => {
    if (isProctoringSettingLocked(key)) {
      (dto as any)[key] = false;
    }
  });
  return dto;
}
